import MarkDownRenderer from '../../../../commonComponents/MarkDownRender/MarkDownRender.Component';

export const markdownContent = `
# **For Loop**

In this example, we'll demonstrate how to use a **For Loop** with API calls in a flow.

We'll work with two APIs:

1. **Users API** – Returns an array of user objects.  
2. **Posts API** – Fetches posts for a user using their **userId**.

## **Users API**

This API returns an array of user objects as shown in the screenshot below.

![Users API response](/flows/forLoop/forLoopUsersAPI.png)

## **Posts API**

This API requires **userId** as a path parameter and returns all posts made by that user.  
The URL format uses a variable:

\`\`\`
https://jsonplaceholder.typicode.com/users/{{userId}}/posts
\`\`\`

![Posts API response](/flows/forLoop/forLoopPostsAPI.png)

## **Flow Configuration**

Start by linking the **Users API** to the Start Node.  
Then add a **For Loop** node after the Users API, followed by the **Posts API** node inside the loop.

![Flow setup](/flows/forLoop/forLoopBoard.png)

## **Flow Variables**

Both the Request and For Loop nodes support **Flow Variables**, allowing data to be shared across the flow at runtime via the flow store.

### **Users API – Setting Flow Variables**

In the Users API node, configure the post-response flow variable as:

\`\`\`
userArray: response.body
\`\`\`

This makes the full array of users accessible to subsequent nodes in the flow.

![Users API flow variable](/flows/forLoop/forLoopUsersPostResponseVariables.png)

### **For Loop – Flow Variables**

In the For Loop node, set the input variable to **{{userArray}}**.  
The loop will iterate over each item in this array.  

Inside the loop, you can define dynamic variables such as **index** and **item**. For example:

\`\`\`
userId: item.id
\`\`\`

This **userId** will be used in the Posts API call.

![For Loop flow variables](/flows/forLoop/forLoopEachIterationVariables.png)

## **Using Variables in Posts API**

The Posts API requires a **userId**. Under the Flow Variables section of the Posts API node, set a pre-request variable by choosing the **“Pick From FlowStore”** option to use the **userId** value set in the loop.

![Posts API pre variables](/flows/forLoop/forLoopPostsApiPreVariables.png)

## **Execution Result**

When the flow executes:

- The Users API is called once to get all users.  
- The Posts API is then called once per user (i.e., N times for N users).

The execution flow is shown below:

![Flow execution result](/flows/forLoop/forLoopExecution.png)
`;

const ForLoop = () => {
    return (
        <MarkDownRenderer
            title="For Loop"
            markDownText={markdownContent}
        />
    );
};

export default ForLoop;
